/* eslint-disable no-console */

import { createSelector, createSelectorFactory, SelectorWithProps } from '@ngrx/store';
import { AnyFn, ComparatorFn, MemoizedProjection } from '@ngrx/store/src/selector';

interface MemoizeOptions {
  ignoreProps: boolean;
  projectorFn: Function;
}

export function isEqualCheck(a: any, b: any): boolean {
  return a === b;
}

function isArgumentsChanged(args: any[], lastArguments: any[], comparator: ComparatorFn) {
  if (!lastArguments) return true;

  for (let i = 0; i < args.length; i++) {
    if (!comparator(args[i], lastArguments[i])) {
      return true;
    }
  }
  return false;
}

enum DebugMode {
  Disabled,
  Info,
  Verbose,
}

const debugConfig = {
  mode: DebugMode.Disabled,
};

const debugPropsChanged = (options: MemoizeOptions, props: any, cache: Map<any, any>) => {
  const name = options.projectorFn.name || (<any>options.projectorFn).debugName;
  name
    ? console.log(
        `%c"${name}" projector called props changed`,
        'background: darkorange; padding: 2px 4px;',
      )
    : console.log('%cProjector called props changed', 'background: darkorange;  padding: 2px 4px;');

  if (DebugMode.Verbose <= debugConfig.mode) {
    console.log({ props });
    console.log({ cache });
  }
};

const debugStateChanged = (options: MemoizeOptions, lastStateChunks: any[], stateChunks: any[]) => {
  const name = options.projectorFn.name || (<any>options.projectorFn).debugName;
  name
    ? console.log(
        `%c"${name}" projector called state changed`,
        'background: red; padding: 2px 4px;',
      )
    : console.log('%cProjector called state changed', 'background: red; padding: 2px 4px;');
  if (DebugMode.Verbose <= debugConfig.mode) {
    console.log({ projector: options.projectorFn });
    console.log({ lastStateChunks, stateChunks });
  }
};

const memoize = (options: MemoizeOptions) =>
  function (projectionFn: AnyFn): MemoizedProjection {
    const cache = new Map();
    let lastResult: any = null;
    let overrideResult: any;
    let lastStateChunks: any[];

    function reset() {
      cache.clear();
    }

    function setResult(result: any = undefined) {
      overrideResult = { result };
    }

    function clearResult() {
      overrideResult = undefined;
    }

    function memoized(...args: any[]): any {
      if (overrideResult !== undefined) {
        return overrideResult.result;
      }

      const stateChunks = args.slice(0, -1);
      const props = args.slice(-1)[0];

      const stateChanged = isArgumentsChanged(stateChunks, lastStateChunks, isEqualCheck);
      // console.log(cache.entries())
      if (!stateChanged) {
        if (options.ignoreProps) {
          return lastResult;
        }

        const inCache = cache.has(props);
        if (inCache) return cache.get(props);

        if (DebugMode.Disabled !== debugConfig.mode && lastStateChunks) {
          debugPropsChanged(options, props, cache);
        }
      }

      if (stateChanged) {
        if (DebugMode.Disabled !== debugConfig.mode && lastStateChunks) {
          debugStateChanged(options, lastStateChunks, stateChunks);
        }
        cache.clear();
        lastResult = null;
        lastStateChunks = stateChunks;
      }

      // eslint-disable-next-line prefer-spread
      const newResult = projectionFn.apply(null, arguments);
      cache.set(props, newResult);
      lastResult = newResult;
      return newResult;
    }

    if (DebugMode.Disabled !== debugConfig.mode) {
      memoized.__options__ = options;
    }

    return { memoized, reset, setResult, clearResult };
  };

export const createSimpleSelector: typeof createSelector = function () {
  const stateFn = (
    state: any,
    selectors: any,
    props: any,
    memoizedProjector: MemoizedProjection,
  ) => {
    const stateChunks = (<SelectorWithProps<any, any, any>[]>selectors).map(fn => fn(state, props));
    return memoizedProjector.memoized.apply(null, [...stateChunks, props]);
  };
  const projectorFn = [...arguments].slice(-1)[0];
  // eslint-disable-next-line prefer-spread
  return createSelectorFactory(memoize({ ignoreProps: true, projectorFn }), {
    stateFn,
  }).apply(null, arguments);
};

export const createSelectorWithProps: typeof createSelector = function () {
  const stateFn = (
    state: any,
    selectors: any,
    props: any,
    memoizedProjector: MemoizedProjection,
  ) => {
    const stateChunks = (<SelectorWithProps<any, any, any>[]>selectors).map(fn => fn(state, props));
    return memoizedProjector.memoized.apply(null, [...stateChunks, props]);
  };
  const projectorFn = [...arguments].slice(-1)[0];
  // eslint-disable-next-line prefer-spread
  return createSelectorFactory(memoize({ ignoreProps: false, projectorFn }), {
    stateFn,
  }).apply(null, arguments);
};

export const createFeatureSelector = <T>(path: string) =>
  createSimpleSelector<any, any, T>(
    (state: any) => state[path],
    f => f,
  );

export const namedProjector = <T extends (...args: any) => any>(name: string, projector: T): T => {
  (<any>projector).debugName = name;
  return projector;
};

export { DebugMode as RtNgrxDebugMode, debugConfig as rtNgrxDebugConfig };
